import React from "react";

import { FormErrorText } from "../../../../../components/form/FormErrorText";
import { Input } from "../../../../../components/input";
import { Label } from "../../../../../components/label";
import { Switch } from "../../../../../components/switch";
import { US_PHONE_COUNTRY_CODE, US_PHONE_FORMAT } from "../../../data/phoneQuestion.constants";
import type { QuestionTypeRendererProps } from "./shared";

type ContactQuestionType = "email" | "phone" | "yes_no";

type ContactQuestionProps<TType extends ContactQuestionType> = QuestionTypeRendererProps<TType> & {
  kind: TType;
};

export function ContactQuestion<TType extends ContactQuestionType>({
  question,
  isPreview,
  yesNoLabelErrorMessage,
  onUpdateQuestion,
  kind,
}: ContactQuestionProps<TType>) {
  const { data } = question;
  const updateQuestionData = (updates: Partial<typeof data>) => {
    onUpdateQuestion({
      ...question,
      data: {
        ...data,
        ...updates,
      },
    });
  };

  if (isPreview) {
    if (kind === "email") {
      return (
        <Input
          type="email"
          placeholder="Enter email"
          disabled
        />
      );
    }

    if (kind === "phone") {
      return (
        <Input
          placeholder="Enter phone number"
          disabled
        />
      );
    }

    return (
      <div className="flex space-x-4">
        <label className="flex items-center space-x-2">
          <input
            type="radio"
            disabled
          />
          <span>{data.yes_label || "Yes"}</span>
        </label>
        <label className="flex items-center space-x-2">
          <input
            type="radio"
            disabled
          />
          <span>{data.no_label || "No"}</span>
        </label>
      </div>
    );
  }

  if (kind === "email") {
    return (
      <div className="flex items-center space-x-2">
        <Switch
          checked={data.allow_multiple || false}
          onCheckedChange={(checked: boolean) => updateQuestionData({ allow_multiple: checked })}
        />
        <Label>Allow multiple emails</Label>
      </div>
    );
  }

  if (kind === "phone") {
    return (
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Country code</Label>
          <Input
            value={data.country_code ?? US_PHONE_COUNTRY_CODE}
            onChange={(event) => updateQuestionData({ country_code: event.target.value })}
            className="mt-1"
          />
        </div>
        <div>
          <Label>Format</Label>
          <Input
            value={US_PHONE_FORMAT}
            disabled
            className="mt-1"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-4">
      <div>
        <Label>Yes label</Label>
        <Input
          value={data.yes_label || "Yes"}
          onChange={(event) => updateQuestionData({ yes_label: event.target.value })}
          className={`mt-1 ${yesNoLabelErrorMessage?.yesLabel ? "border-red-500 dark:border-red-500" : ""}`}
        />
        <FormErrorText message={yesNoLabelErrorMessage?.yesLabel} />
      </div>
      <div>
        <Label>No label</Label>
        <Input
          value={data.no_label || "No"}
          onChange={(event) => updateQuestionData({ no_label: event.target.value })}
          className={`mt-1 ${yesNoLabelErrorMessage?.noLabel ? "border-red-500 dark:border-red-500" : ""}`}
        />
        <FormErrorText message={yesNoLabelErrorMessage?.noLabel} />
      </div>
    </div>
  );
}

export default ContactQuestion;
